import { RateGroupType } from "@intelligems/ig-types/dtos/shipping-rate-groups";
import { ShippingRateGroupFe } from "@intelligems/ig-types/fe-only/shipping-rate-groups";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";

/** The rate type a test group was set up with; custom wins, as the app saves it. */
export const variationRateType = (
  variation: VariationFe
): RateGroupType | null => {
  const groups = variation.shippingRateGroups ?? [];
  if (groups.some((group) => group.groupType === "custom")) {
    return "custom";
  }
  // A tiered setup's free rate is stored as a threshold-only group.
  if (groups.some((group) => group.groupType === "tieredByPriceOrWeight")) {
    return "tieredByPriceOrWeight";
  }
  return groups[0]?.groupType ?? null;
};

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === "";

/** "Min can't be greater or equal to Max" for a band whose range is empty. */
export const tierRangeError = (group: ShippingRateGroupFe): string | null => {
  const condition = group.rates[0]?.condition;
  if (group.groupType !== "tieredByPriceOrWeight" || !condition) {
    return null;
  }
  if (isBlank(condition.max)) {
    return null;
  }
  const min = Number(condition.min || 0);
  const max = Number(condition.max);
  return min >= max ? "Min can't be greater or equal to Max" : null;
};

/** Why one rate group is incomplete, or null. Mirrors the app's save rules. */
const groupIncomplete = (group: ShippingRateGroupFe): boolean => {
  if (group.groupType === "custom") {
    return false;
  }
  return group.rates.some(
    (rate) =>
      !rate.name ||
      isBlank(rate.amount) ||
      (rate.rateType === "threshold" && Number(rate.amount) <= 0)
  );
};

/**
 * Why the "Set rates per group" step cannot be saved, or null: a group has
 * no rate, a rate is missing its required figures, a tier range is empty, a
 * custom test has no description, or (Markets) a rate has no Applies to.
 */
export const ratesStepReason = (
  variations: VariationFe[],
  customTestDesc: string | null | undefined,
  requireScopes: boolean
): string | null => {
  for (const variation of variations) {
    const groups = variation.shippingRateGroups ?? [];
    const name = variation.name || "A test group";
    if (groups.length === 0) {
      return "Create rates for each test group";
    }
    if (variationRateType(variation) === "custom") {
      if (!customTestDesc?.trim()) {
        return "Describe the shipping test you wish to run.";
      }
      continue;
    }
    for (const group of groups) {
      const rangeError = tierRangeError(group);
      if (rangeError) {
        return rangeError;
      }
      if (groupIncomplete(group)) {
        return `${name} has a rate without a name or amount.`;
      }
      if (requireScopes && !group.scopes?.length) {
        return `${name} has a rate without Applies to.`;
      }
    }
  }
  return null;
};

/**
 * Display order of a group's rates: tier bands first, the tiered setup's free
 * rate last, as the design lays them out. Returns indexes into the groups.
 */
export const rateGroupDisplayOrder = (
  groups: ShippingRateGroupFe[]
): number[] => {
  const indexes = groups.map((_, index) => index);
  if (!groups.some((group) => group.groupType === "tieredByPriceOrWeight")) {
    return indexes;
  }
  return [
    ...indexes.filter((index) => groups[index].groupType !== "thresholdOnly"),
    ...indexes.filter((index) => groups[index].groupType === "thresholdOnly"),
  ];
};

/**
 * How many chips, in order, fit on one line of `available` px. When some are
 * left over, the "+N more" chip (`moreWidth`) must fit on the line too.
 */
export const chipsThatFit = (
  widths: number[],
  moreWidth: number,
  available: number,
  gap: number
): number => {
  let used = 0;
  for (let count = 0; count < widths.length; count++) {
    used += (count ? gap : 0) + widths[count];
    const rest = count + 1 < widths.length ? gap + moreWidth : 0;
    if (used + rest > available) {
      return count;
    }
  }
  return widths.length;
};
